import React from 'react';
import styled from 'styled-components';
import { IconGraphy } from '@leapeasy/ui-kit';
import { getApplicationCreatedDateRangeFilterLabel } from 'utils/applicationCreatedDateRangeFilter';

export const APPLICATION_LIST_FILTER_CHIP_LEASE = 'lease';
export const APPLICATION_LIST_FILTER_CHIP_CREATED = 'created';

export function parseApplicationListFilterChipId(chipId) {
  if (chipId === APPLICATION_LIST_FILTER_CHIP_LEASE) return { type: 'lease' };
  if (chipId === APPLICATION_LIST_FILTER_CHIP_CREATED) return { type: 'created' };
  const idx = chipId.indexOf(':');
  if (idx < 1) return null;
  return { type: chipId.slice(0, idx), value: chipId.slice(idx + 1) };
}

function normalizeStringArray(value) {
  if (!Array.isArray(value)) return [];
  return value.filter((v) => v != null && String(v).trim() !== '');
}

function formatLeaseChipLabel(lease) {
  const startDate = lease?.startDate;
  const endDate = lease?.endDate;
  if (!startDate && !endDate) return '';
  const fmt = (d) => {
    try {
      const t = new Date(d).getTime();
      if (!Number.isFinite(t)) return String(d);
      return new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return String(d);
    }
  };
  const parts = [];
  if (startDate) parts.push(`Start ${fmt(startDate)}`);
  if (endDate) parts.push(`End ${fmt(endDate)}`);
  return parts.join(' — ');
}

export function buildApplicationListFilterChips({
  selectedStage,
  selectedAppType,
  selectedLeaseDate,
  selectedCreatedDate,
  selectedTenantCount,
  stageOptions,
  appTypeOptions,
}) {
  const chips = [];
  const stageText = (v) => stageOptions.find((o) => o.value === v)?.text ?? v;
  const appTypeText = (v) => appTypeOptions.find((o) => o.value === v)?.text ?? String(v ?? '');

  normalizeStringArray(selectedStage).forEach((v) => {
    chips.push({ id: `stage:${v}`, label: `Stage: ${stageText(v)}` });
  });

  normalizeStringArray(selectedAppType).forEach((v) => {
    chips.push({ id: `appType:${v}`, label: `Application Type: ${appTypeText(v)}` });
  });

  const leaseLabel = formatLeaseChipLabel(selectedLeaseDate);
  if (leaseLabel) {
    chips.push({
      id: APPLICATION_LIST_FILTER_CHIP_LEASE,
      label: `Lease: ${leaseLabel}`,
    });
  }

  const createdLabel = getApplicationCreatedDateRangeFilterLabel(selectedCreatedDate);
  if (createdLabel) {
    chips.push({
      id: APPLICATION_LIST_FILTER_CHIP_CREATED,
      label: `Created: ${createdLabel}`,
    });
  }

  normalizeStringArray(selectedTenantCount).forEach((v) => {
    chips.push({ id: `tenant:${v}`, label: `Total Resident: ${v}` });
  });

  return chips;
}

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  padding: 6px 8px 6px 10px;
  border-radius: 8px;
  background: #e8d6eb;
  color: #2e0f40;
  box-sizing: border-box;
`;

const ChipLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #2e0f40;
  line-height: 1.3;
  word-break: break-word;
`;

const RemoveChipButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #702572;
  line-height: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.85;
  }
`;

const ClearAllButton = styled.button`
  margin: 0;
  padding: 4px 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #702572;

  &:hover {
    text-decoration: underline;
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

export function ApplicationListActiveFilters({ chips, onRemoveChip, onClearAll }) {
  if (!chips?.length) return null;

  return (
    <ChipsRow data-llp-active-filters>
      {chips.map((chip) => (
        <Chip key={chip.id}>
          <ChipLabel>{chip.label}</ChipLabel>
          <RemoveChipButton
            type="button"
            aria-label={`Remove ${chip.label}`}
            onClick={() => onRemoveChip(chip.id)}
          >
            <IconGraphy icon="General.Clear" width={10} height={10} />
          </RemoveChipButton>
        </Chip>
      ))}
      <ClearAllButton type="button" onClick={onClearAll}>
        Clear all
      </ClearAllButton>
    </ChipsRow>
  );
}
